import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Folder, Trash2, Eye, RefreshCcw, X, Grid,
  ChevronLeft, File, AlertCircle, Users, BookOpen,
  LayoutGrid, LayoutList, Cloud, Database,
  Image as ImageIcon, User, School, ChevronRight
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { getFolders, deleteItem } from "../../services/fileManager";
import { getTeachers } from "../../services/teacherService";
import './MangerLayer.css';
import http from "../../services/http";

export const ManagerLayer = () => {
  const [rawData, setRawData] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTeacher, setFilterTeacher] = useState("all");
  const [deletingItems, setDeletingItems] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  
  // Gallery state
  const [gallery, setGallery] = useState({
    images: [],
    currentIndex: 0,
    isOpen: false
  });

  // ========== GET AUTH TOKEN (customize as per your app) ==========
  const getAuthToken = () => {
    // Replace with your actual token retrieval logic (e.g., from localStorage, context, etc.)
    return localStorage.getItem('authToken') || '';
  };

  // ========== RIGHT CLICK BLOCKER ==========
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // ========== LOAD DATA ==========
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [folderRes, teacherRes] = await Promise.all([
        getFolders(13),
        getTeachers()
      ]);
      
      if (folderRes?.data?.status) {
        setRawData(folderRes.data.data.folders || []);
      } else {
        setRawData([]);
        if (!silent) toast.error(folderRes?.data?.message || "Failed to load folders");
      }
      
      setTeachers(teacherRes?.data || []);
    } catch (err) {
      console.error("Load error:", err);
      if (!silent) toast.error("Failed to sync with cloud");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ========== DELETE (CLASS / STUDENT) ==========
  const handleDelete = async (type, id, className = null) => {
    if (!id) {
      toast.error(`Cannot delete: Missing ${type === 'file' ? 'student ID' : 'class ID'}`);
      return;
    }

    const confirmMsg = type === 'file'
      ? `⚠️ Delete student record (ID: ${id})? This action cannot be undone.`
      : `⚠️ Delete entire class "${className}"? All student data will be lost.`;
    
    if (!window.confirm(confirmMsg)) return;

    setDeletingItems(prev => ({ ...prev, [id]: true }));
    
    const loadingToast = toast.loading(`Deleting ${type === 'file' ? 'student record' : 'class folder'}...`);
    
    try {
      const res = await deleteItem(type, id);
      
      if (res.status) {
        toast.success(`${type === 'file' ? 'Student record' : 'Class'} deleted successfully`,
          { id: loadingToast, icon: '🗑️' }
        );
        await loadData(true);
        if (type === 'folder' && currentFolder?.class_id === id) {
          setCurrentFolder(null);
        }
      } else {
        toast.error(res.message || "Delete failed", { id: loadingToast });
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Network error during deletion", { id: loadingToast });
    } finally {
      setDeletingItems(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  // ========== PDF GENERATION (WITH AUTHORIZATION) ==========
// ========== PDF GENERATION (Handles Invalid Token & Errors) ==========
const handleGeneratePDF = async (classId, className) => {
  const loadingToast = toast.loading(`Generating PDF report for ${className}...`);
  try {
    // Use the same http instance that already has auth headers
    const response = await http.get("Studentreport.php", {
      params: { class_id: classId },
      responseType: "blob", // Important to get binary data
    });

    // Check if response is actually a PDF blob
    const contentType = response.headers["content-type"];
    if (contentType && contentType.includes("application/pdf")) {
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Class_${classId}_Report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully", { id: loadingToast });
    } else {
      // Might be JSON error disguised as blob (status false)
      const text = await response.data.text(); // if blob, convert to text
      const json = JSON.parse(text);
      if (json.status === false) {
        toast.error(json.message || "Authentication failed", { id: loadingToast });
      } else {
        toast.error("Unexpected response from server", { id: loadingToast });
      }
    }
  } catch (error) {
    console.error("PDF Error:", error);
    toast.error(error.message || "Failed to generate PDF report", { id: loadingToast });
  }
};

  // ========== FILTERING & STATS ==========
  const filteredFolders = useMemo(() => {
    if (!Array.isArray(rawData)) return [];
    
    return rawData.filter(folder => {
      if (!folder.students || !Array.isArray(folder.students)) return false;
      
      const filteredStudents = folder.students.filter(student => {
       const matchesSearch =
  student.student_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  student.teacher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  folder.class_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTeacher = filterTeacher === "all" || student.teacher_name === filterTeacher;
        return matchesSearch && matchesTeacher;
      });
      
      return filteredStudents.length > 0;
    }).map(folder => ({
      ...folder,
      filteredStudents: folder.students.filter(student => {
        const matchesSearch =
          student.student_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.teacher_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTeacher = filterTeacher === "all" || student.teacher_name === filterTeacher;
        return matchesSearch && matchesTeacher;
      })
    }));
  }, [rawData, searchTerm, filterTeacher]);

  const currentFolderData = useMemo(() => {
    if (!currentFolder) return null;
    return filteredFolders.find(f => f.class_id === currentFolder.class_id);
  }, [currentFolder, filteredFolders]);

  // ========== IMAGE GALLERY LOGIC ==========
  const buildGalleryFromCurrentView = useCallback(() => {
    if (!currentFolderData) return [];
    return currentFolderData.filteredStudents
      .filter(student => student.photo)
      .map(student => ({
        url: student.photo,
        studentId: student.student_id,
        studentName: student.student_name
      }));
  }, [currentFolderData]);

  const handleOpenGallery = (clickedStudent) => {
    const allImages = buildGalleryFromCurrentView();
    const index = allImages.findIndex(img => img.studentId === clickedStudent.student_id);
    if (index !== -1) {
      setGallery({ images: allImages, currentIndex: index, isOpen: true });
    } else {
      toast.error("No image available for this student");
    }
  };

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!gallery.isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setGallery(prev => ({
          ...prev,
          currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
        }));
      } else if (e.key === 'ArrowRight') {
        setGallery(prev => ({
          ...prev,
          currentIndex: (prev.currentIndex + 1) % prev.images.length
        }));
      } else if (e.key === 'Escape') {
        setGallery(prev => ({ ...prev, isOpen: false }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gallery.isOpen, gallery.images.length]);

  const getTotalStats = useMemo(() => {
    let totalStudents = 0;
    let totalClasses = filteredFolders.length;
    filteredFolders.forEach(folder => {
      totalStudents += folder.filteredStudents?.length || 0;
    });
    return { totalStudents, totalClasses };
  }, [filteredFolders]);

  // ========== RENDER ==========
  return (
    <div className="premium-manager">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '12px',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <header className="premium-header">
        <div className="header-gradient">
          <div className="logo-area" onClick={() => setCurrentFolder(null)} style={{ cursor: 'pointer' }}>
            <div className="logo-icon">
              <School size={24} />
            </div>
            <div className="logo-text">
              <h5>SchoolCloud</h5>
              <p>Premium File Manager</p>
            </div>
          </div>

          <div className="header-controls">
            <div className="premium-search">
              <Search className="search-icon" size={18} />
              <input
placeholder="Search by Student ID, Student Name, Class..."                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <X
                  className="clear-search"
                  size={16}
                  onClick={() => setSearchTerm("")}
                  role="button"
                />
              )}
            </div>

            {/* Teacher Filter Dropdown */}
            <select
              className="premium-select"
              value={filterTeacher}
              onChange={(e) => setFilterTeacher(e.target.value)}
            >
              <option value="all">All Teachers ({teachers.length})</option>
              {teachers.map(t => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>

            {/* PDF DOWNLOAD BUTTON - shows only when a class folder is open */}
            {currentFolder && (
              <button
                onClick={() => handleGeneratePDF(currentFolder.class_id, currentFolder.class_name)}
                className="premium-btn btn-pdf"
                title="Download PDF Report for this class"
              >
                <File size={18} /> Download PDF
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="premium-main">
        <aside className="premium-sidebar">
          <div className="sidebar-header">
            <h5>Navigation</h5>
          </div>
          <button
            className={`sidebar-item ${!currentFolder ? 'active' : ''}`}
            onClick={() => setCurrentFolder(null)}
          >
            <Grid size={18} className="sidebar-icon" />
            All Classes
            <span className="sidebar-badge">{getTotalStats.totalClasses}</span>
          </button>

          {currentFolder && (
            <>
              <button
                className="sidebar-item back-button"
                onClick={() => setCurrentFolder(null)}
              >
                <ChevronLeft size={18} className="sidebar-icon" />
                Back to Classes
              </button>
              <div className="current-folder-info">
                <Folder size={16} />
                <span>{currentFolder.class_name}</span>
              </div>
            </>
          )}

          <div className="sidebar-stats">
            <div className="stat-item">
              <Database size={14} />
              <span>Total Storage</span>
              <strong>{getTotalStats.totalStudents} Files</strong>
            </div>
          </div>
        </aside>

        <section className="premium-content">
          <div className="premium-actions">
            <div className="stats-badge">
              <div className="stat-card">
                <BookOpen size={16} />
                <span>{getTotalStats.totalClasses} Classes</span>
              </div>
              <div className="stat-card">
                <Users size={16} />
                <span>{getTotalStats.totalStudents} Students</span>
              </div>
            </div>

            <div className="action-group">
              <div className="view-toggle">
                <button
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <LayoutList size={16} />
                </button>
              </div>
              <button className="premium-btn btn-primary" onClick={() => loadData()}>
                <RefreshCcw size={18} className={loading ? 'spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-premium">
              <div className="loading-spinner-premium"></div>
              <p>Loading your data...</p>
              <span className="loading-text">Please wait while we fetch your files</span>
            </div>
          ) : filteredFolders.length === 0 ? (
            <div className="empty-state-premium">
              <Cloud size={64} />
              <h5>No data found</h5>
              <p>Try adjusting your search or refresh the page</p>
            </div>
          ) : !currentFolder ? (
            <div className="folders-section-premium">
              <div className="section-header">
                <h5 className="section-title">
                  <Folder size={20} /> All Classes
                </h5>
                <div className="section-decoration"></div>
              </div>
              <div className="folders-grid-premium">
                {filteredFolders.map((folder) => (
                  <div key={folder.class_id} className="folder-card-premium">
                    <div
                      onClick={() => setCurrentFolder(folder)}
                      className="folder-content"
                    >
                      <div className="folder-icon-premium">
                        <Folder size={48} />
                      </div>
                      <div className="folder-info-premium">
                        <h5>{folder.class_name}</h5>
                        <div className="folder-meta">
                          <Users size={14} />
                          <p>{folder.filteredStudents?.length || 0} Students</p>
                        </div>
                        <small>Class ID: {folder.class_id}</small>
                      </div>
                    </div>
                    <div className="folder-actions-premium">
                      <button
                        onClick={() => handleGeneratePDF(folder.class_id, folder.class_name)}
                        className="icon-btn pdf"
                        title="Generate PDF Report"
                      >
                        <File size={16} />
                      </button>
                      {/* <button
                        onClick={() => handleDelete('folder', folder.class_id, folder.class_name)}
                        className="icon-btn delete"
                        disabled={deletingItems[folder.class_id]}
                        title="Delete class"
                      >
                        {deletingItems[folder.class_id] ? (
                          <div className="small-spinner"></div>
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="files-section-premium">
              <div className="files-header-premium">
                <div className="header-left">
                  <button
                    className="back-button-icon"
                    onClick={() => setCurrentFolder(null)}
                    title="Back to classes"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <div className="folder-title">
                    <Folder size={20} />
                    <h5>{currentFolder.class_name}</h5>
                    <ChevronRight size={16} className="breadcrumb-sep" />
                    <span className="current-breadcrumb">Students</span>
                  </div>
                </div>
                <span className="file-count">
                  <Users size={14} />
                  {currentFolderData?.filteredStudents?.length || 0} Students
                </span>
              </div>

              {viewMode === 'grid' ? (
                <div className="files-grid-premium">
                  {currentFolderData?.filteredStudents?.map((student, idx) => (
                    <div key={idx} className="file-card-premium">
                      <div
                        className="file-preview-box"
                        onClick={() => handleOpenGallery(student)}
                      >
                        {student.photo ? (
                          <img
                            src={student.photo}
                            alt={student.student_id}
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="no-image">
                            <ImageIcon size={48} />
                            <span>No Image</span>
                          </div>
                        )}
                      </div>
                      <div className="file-info">
                        <p><b>Student ID : </b><span className="teacher-badge" title={student.student_id}>{student.student_id}</span></p>
                        <p><b>Student Name : </b>
                          <span className="teacher-badge">{student.student_name || 'No Student Name'}</span>
                        </p>
                      </div>
                      <div className="file-actions-premium">
                        <button
                          onClick={() => handleOpenGallery(student)}
                          className="icon-btn view"
                          title="View image"
                        >
                          <Eye size={16} />
                        </button>
                        {/* <button
                          onClick={() => handleDelete('file', student.student_id)}
                          className="icon-btn delete"
                          disabled={deletingItems[student.student_id]}
                          title="Delete student"
                        >
                          {deletingItems[student.student_id] ? (
                            <div className="small-spinner"></div>
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button> */}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="premium-table-wrapper">
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th>Student ID</th>
                        <th>Student Name</th>
                        <th>Teacher</th>
                        <th>Photo</th>
                        {/* <th>Actions</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {currentFolderData?.filteredStudents?.map((student, idx) => (
                        <tr key={idx}>
                          <td data-label="Student ID">
                            <div className="student-id-cell">
                              <User size={14} />
                              {student.student_id}
                            </div>
                          </td>
                          <td data-label="Student Name">
                            <div className="student-id-cell">
                              <User size={14} />
                              {student.student_name || 'No Student Name'}
                            </div>
                          </td>
                          <td data-label="Teacher">{student.teacher_name || 'No teacher'}</td>
                          <td data-label="Photo">
                            <button
                              className="view-photo-btn"
                              onClick={() => handleOpenGallery(student)}
                            >
                              <Eye size={14} /> View
                            </button>
                          </td>
                          {/* <td data-label="Actions">
                            <div className="table-actions">
                              <button
                                onClick={() => handleDelete('file', student.student_id)}
                                className="icon-btn delete"
                                disabled={deletingItems[student.student_id]}
                              >
                                {deletingItems[student.student_id] ? (
                                  <div className="small-spinner"></div>
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </button>
                            </div>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* IMAGE GALLERY MODAL */}
      {gallery.isOpen && (
        <div className="gallery-modal-overlay" onClick={() => setGallery(prev => ({ ...prev, isOpen: false }))}>
          <div className="gallery-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-close" onClick={() => setGallery(prev => ({ ...prev, isOpen: false }))}>
              <X size={24} />
            </button>
            <button
              className="gallery-nav prev"
              onClick={() => setGallery(prev => ({
                ...prev,
                currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
              }))}
            >
              <ChevronLeft size={32} />
            </button>
            <div className="gallery-image-wrapper">
              <img
                src={gallery.images[gallery.currentIndex]?.url}
                alt={gallery.images[gallery.currentIndex]?.studentId}
                className="gallery-image"
              />
              <div className="gallery-caption">
                <p><strong>Student ID:</strong> {gallery.images[gallery.currentIndex]?.studentId}</p>
                <p><strong>Student Name:</strong> {gallery.images[gallery.currentIndex]?.studentName}</p>
                <p className="gallery-counter">
                  {gallery.currentIndex + 1} / {gallery.images.length}
                </p>
              </div>
            </div>
            <button
              className="gallery-nav next"
              onClick={() => setGallery(prev => ({
                ...prev,
                currentIndex: (prev.currentIndex + 1) % prev.images.length
              }))}
            >
              <ChevronRight size={32} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


